/**
 * Contains the form validation and input handling rules.
 * Includes functions to validate different types of inputs (text, email, file, etc.),
 * to handle custom UI elements like styled selects and checkboxes, and to manage form submission
 * state based on input validity and user consent.
 */
(function ($) {
    'use strict';

    var validClass = 'success';
    var invalidClass = 'has-error';

    var validators = [];
    validators['text'] = [validateProvided, validatePattern];
    validators['email'] = [validateProvided, validatePattern, validateMatch];
    validators['file'] = [validateFile];
    validators['select-one'] = [validateProvided];
    validators['textarea'] = [validateProvided];
    validators['radio'] = [validateChecked];
    validators['checkbox'] = [validateChecked];
    validators['number'] = [validateProvided, validateNumberRange];
    validators['hidden'] = [validateProvided];

    /**
     * Checks if a required input is provided.
     * @param {jQuery} $input - The input element to validate.
     * @returns {string} - The validation message if invalid, otherwise an empty string.
     */
    function validateProvided($input) {
        var message = '';
        if ($input.prop('required')) {
            // Use .val() directly without trimming, as it's not necessary for select elements and is error-prone with hidden fields
            var value = $input.val();

            // Specifically handle cases where the select might have a placeholder or default disabled option that should not be considered a valid selection
            if (!value || value === "" || $input.find('option').first().is(':disabled') && $input.find('option').first().val() === value) {
                message = $input.data('required-message');
            }
        }
        return message;
    }

    /**
     * Checks if a valid email pattern is matched.
     * @param {jQuery} $input - The input element to validate.
     * @returns {string} - The validation message if invalid, otherwise an empty string.
     */
    function validatePattern($input) {
        var message = '';
        var value = $input.val();
        if (value) {
            var pattern = $input.data('pattern');
            if (pattern) {
                try {
                    var regex = new RegExp(pattern);
                    if (!regex.test(value.trim())) {
                        message = $input.data('validate-message');
                    }
                } catch (error) {
                    message = $input.data('validate-message');
                }
            }
        }
        return message;
    }

    /**
     * Checks if a text input matches a specified input's value.
     * @param {jQuery} $input - The input element to validate.
     * @returns {string} - The validation message if invalid, otherwise an empty string.
     */
    function validateMatch($input) {
        let message = '';
        let matchInputId = $input.data('match');
        if (matchInputId) {
            let $matchInput = $('#' + matchInputId);

            if ($matchInput.val() !== $input.val()) {
                message = $input.data('match-message');
            }
        }
        return message;
    }

    /**
     * Checks if a number input falls within a specified range.
     * @param {jQuery} $input - The input element to validate.
     * @returns {string} - The validation message if invalid, otherwise an empty string.
     */
    function validateNumberRange($input) {
        var message = '';
        var value = parseInt($input.val(), 10);

        if (!Number.isNaN(value)) {
            var min = parseInt($input.attr('min'), 10);
            var max = parseInt($input.attr('max'), 10);

            if (value < min || value > max) {
                message = $input.data('validate-message');
            }
        }

        return message;
    }

    /**
     * Checks if a file input meets the specified requirements,
     * including file size and maximum number of files.
     * @param {jQuery} $input - The input element to validate.
     * @returns {string} - The validation message if invalid, otherwise an empty string.
     */
    function validateFile($input) {
        var message = '';
        var files = $input[0].files;
        var filesAllowed = $input.data('filesallowed');

        if ($input.prop('required') && files.length === 0) { // check for the required file
            return $input.data('required-message');
        }

        if (filesAllowed && files.length > filesAllowed) { // check if more files are chosen than allowed
            return $input.data('validate-filesallowed-message');
        }

        for (var i = 0; i < files.length; i++) { // checks for file size
            var size = files[i].size;
            var maxSize = $input.data('filemaxsize');
            var maxSizeUnit = $input.data('filesizeunit');
            if (!isFileSizeValid(size, maxSize, maxSizeUnit)) {
                return $input.data('validate-message');
            }
        }

        return message;
    }

    /**
     * Checks if a file size is valid based on the specified limits.
     * @param {number} size - The size of the file in bytes.
     * @param {number} maxSize - The maximum allowed size for the file.
     * @param {string} maxSizeUnit - The unit of the maximum allowed size (e.g., 'KB' or 'MB').
     * @returns {boolean} - True if the file size is valid, false otherwise.
     */
    function isFileSizeValid(size, maxSize, maxSizeUnit) {
        var sizeInKB = size / 1024;
        var sizeInMB = sizeInKB / 1024;
        var roundedSize =
            Math.round((maxSizeUnit == 'MB' ? sizeInMB : sizeInKB) * 100) / 100;
        var roundedMaxSize = Math.round(maxSize * 100) / 100;
        return roundedSize <= roundedMaxSize;
    }

    /**
     * Checks if a checkbox or radio input is checked.
     * @param {jQuery} $input - The input element to validate.
     * @returns {string} - The validation message if invalid, otherwise an empty string.
     */
    function validateChecked($input) {
        var message = '';
        if ($input.prop('required')) {
            var $form = $input.closest('form');
            var name = $input.attr('name');
            var valid = $form.find('[name="' + name + '"]').filter(':checked').length > 0;
            message = !valid ? $input.data('required-message') : message;
        }
        return message;
    }

    /**
     * Validates an input element.
     * @returns {boolean} - True if the input is valid, false otherwise.
     */
    $.fn.validate = function() {
        const $input = this;
        const isCustomOptionInput = $input.is('.form-group-custom-option__input');
        const $formGroup = $input.closest('.form-group');
        const validatorsToRun = validators[$input[0].type] || [];
        const $form = $(this).closest('form');
        let message = "";

        // Custom option validation should be skipped if not opted for (i.e., visible)
        if (!isCustomOptionInput || (isCustomOptionInput && $input.is(':visible'))) {

            for (let i = 0; i < validatorsToRun.length; i++) {
                message = validatorsToRun[i]($input);
                if (message) {
                    $formGroup.find('.validation-error span').text(message);
                    $formGroup.find('.validation-error-sr').text(message);
                    $formGroup.find('.form-control').attr('aria-invalid', 'true');
                    $formGroup.removeClass(validClass).addClass(invalidClass);

                    return false;
                }
            }

            // Clear any error state if validation is passed
            $formGroup.find('.validation-error span').text('');
            $formGroup.find('.validation-error-sr').text('');
            if ($formGroup.hasClass(invalidClass)) {
                $formGroup.removeClass(invalidClass).addClass(validClass);
                $formGroup.find('.form-control').attr('aria-invalid', 'false');
            }
        }

        // TODO: Currently submit button will be enabled always on forms - remove functionality if not used for long time
        // femcare.shared.util.updateFormButtonState($form);

        return !message; // return true if no message (valid), false otherwise (invalid)
    };


    /**
     * Resets an input element.
     */
    $.fn.resetInput = function () {
        var $input = this;
        var $formGroup = $input.closest('.form-group');
        $input.val('');
        $formGroup.removeClass(invalidClass).removeClass(validClass);
    }
})(jQuery);
